import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { EScheduleTypes } from '../../../shared/models/eop_types.enum';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { fileUploadValidator } from '../../../shared/helpers/fileUploadValidation';
import { UserDataService } from '../../../shared/services/userData.service';
import { DateUtils } from '../../../shared/helpers/dateUtils';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { ScheduleService } from '../../../shared/services/schedule.service';
import { Router } from '@angular/router';
import { getPlanSlugFromUrl, REPORT_TYPES_BY_PLAN } from '../../eop-config/eop-page-config';
declare var initFlowbite: any;

@Component({
  selector: 'new-report',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent
  ],
  templateUrl: './new-report.component.html',
  styleUrl: './new-report.component.css'
})
export class NewReportComponent implements OnInit {
  @ViewChild('datepickerStartInput') datepickerStartInput!: ElementRef;
  // @ViewChild('datepickerEndInput') datepickerEndInput!: ElementRef;
  
  icons = Icons;
  reportTypes = EScheduleTypes;
  // Which report types this plan offers — see REPORT_TYPES_BY_PLAN
  availableReportTypes: { value: EScheduleTypes; label: string }[] = [];
  currentBuildingId: string | null = null;
  suggestionsByType: { [key: number]: string[] } = {
    [this.reportTypes["Drills"]]: [
      "Fire Drill Response Report",
      "Seasonal Severe Weather Drill Response Report",
      "Lockdown Drill Response Report",
      "School Bus Evacuation"
    ],
    [this.reportTypes["Training"]]: [
      "Annual Safety (EOP) Plan Review",
      "Annalyn's Law (Juvenile Sex Offender)",
      "Erin's Law (Recognize and Avoid Child Sexual Abuse)",
      "Jamari T. Williams Student Bullying Prevention Act",
      "Mandatory Reporting (Reporting Child Abuse)",
      "Mental Health Awareness",
      "Suicide Awareness Prevention"
    ],
    [this.reportTypes["Collaboration"]]: [
      "Collaborative Planning Team Organization Meeting",
      "Threat & Assessment Meeting",
      "Goals and Objectives Meeting",
      "Plans Development Meeting",
      "Plans Preparation, Review, & Approval Meeting",
      "Plan Implementation & Maintenance Meeting"
    ]
  };
  
  showSuggestions: boolean = false;
  loading: boolean = false;

  @Input() selectedDate: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  eventForm = new FormGroup({
    name: new FormControl('', Validators.required),
    type_id: new FormControl(EScheduleTypes.Drills, Validators.required), // calendar_logs "Drills" type (id 2)
    start_date: new FormControl('', Validators.required),
    time: new FormControl('08:00', Validators.required),
    duration: new FormControl('1 hour', Validators.required),
    details: new FormControl('', Validators.required),
    files: new FormControl<File[]>([], fileUploadValidator())
  })

  constructor(
    private userDataService: UserDataService,
    private scheduleService: ScheduleService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.availableReportTypes = REPORT_TYPES_BY_PLAN[getPlanSlugFromUrl(this.router.url)]
      .map(value => ({ value, label: EScheduleTypes[value] }));
    if (this.selectedDate) {
      this.eventForm.get('start_date')?.setValue(this.selectedDate);
    }
    this.currentBuildingId = this.userDataService.getBuildingId();
  }
  
  async ngAfterViewInit(): Promise<void> {
    // Dynamically import the Datepicker to avoid server-side loading
    const { Datepicker } = await import('flowbite-datepicker');
    if (this.datepickerStartInput) {
      new Datepicker(this.datepickerStartInput.nativeElement, {
        autohide: true,
        format: 'mm/dd/yyyy'
      });
      if (this.selectedDate) { // Check if selectedDate is set, make it a default value for start date
        this.datepickerStartInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
      }
      // Add event listener for date change
      // This will update the form control with the formatted date when the date is changed
      this.datepickerStartInput.nativeElement.addEventListener('changeDate', (event: any) => {
        const formattedDate = DateUtils.formatDateForBackend(event.target.value);
        this.eventForm.get('start_date')?.setValue(formattedDate);
      });
    }
    // if (this.datepickerEndInput) {
    //   new Datepicker(this.datepickerEndInput.nativeElement, {
    //     autohide: true,
    //     format: 'mm/dd/yyyy'
    //   });
    //   if (this.selectedDate) {
    //     this.datepickerEndInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
    //   }
    //   this.datepickerEndInput.nativeElement.addEventListener('changeDate', (event: any) => {
    //     const formattedDate = DateUtils.formatDateForBackend(event.target.value);
    //     this.eventForm.get('end_date')?.setValue(formattedDate);
    //   });
    // }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.eventForm.get('files')?.setValue(files);
      this.eventForm.get('files')?.markAsDirty();
      this.eventForm.get('files')?.updateValueAndValidity();
    }
  }

  get filteredSuggestions(): string[] {
    const typeId = this.eventForm.get('type_id')?.value;
    const query = this.eventForm.get('name')?.value?.toLowerCase() || '';
  
    const typeSuggestions = typeId !== null && typeId !== undefined ? this.suggestionsByType[typeId] || [] : [];
    return typeSuggestions.filter(s => s.toLowerCase().includes(query));
  }
  
  selectSuggestion(value: string): void {
    this.eventForm.get('name')?.setValue(value);
    this.showSuggestions = false;
  }
  
  hideSuggestions(): void {
    setTimeout(() => this.showSuggestions = false, 150);
  }

  saveNew(): void {
    if (this.eventForm.valid && this.currentBuildingId) {
      this.loading = true;
      const eventForm = new FormData();
  
      // Convert the form group to FormData
      Object.keys(this.eventForm.controls).forEach((key) => {
        const controlValue = this.eventForm.get(key)?.value;
    
        if (key === 'files') {
            if (controlValue && controlValue.length > 0) {
                // Handle file upload for 'files' field
                controlValue.forEach((file: File) => {
                    if (file instanceof Blob) {
                        eventForm.append(key, file, file.name); // Append each file
                    } else {
                        console.error("Invalid file type detected:", file);
                    }
                });
            }
        // If no files are present, do not append anything for 'files'.
        } else {
            // Add other form fields (non-file fields)
            if (controlValue !== undefined && controlValue !== null) {
                eventForm.append(key, controlValue);
            } else {
                // If the field is null or undefined, append an empty string to match FastAPI's expectation
                eventForm.append(key, '');
            }
        }
    });
  
      this.scheduleService.createNewSchedule(this.currentBuildingId, eventForm).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after creating a new event.' });
          }
          console.log(response);
        },
        error: (error) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: error.message });
        }
      })
    } 
  }

  closeCreate(): void {
    this.onClose.emit();
  }

}
